import type { ReactNode } from 'react';
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';
import { Callout } from './Callout';
import { Matrix } from './Matrix';
import { Metric } from './Metric';
import { Rich } from './Rich';

export type Block =
  | { kind: 'text'; text: string }
  | { kind: 'metrics'; items: { value: string; label: string; tone?: 'accent' | 'plain' | 'muted' }[] }
  | {
      kind: 'matrix';
      head: string[];
      rows: string[][];
      accentRows?: number[];
      numeric?: number[];
      minWidth?: string;
    }
  | { kind: 'callout'; text: string; title?: string; tone?: 'accent' | 'caveat' }
  | { kind: 'cards'; items: { title: string; text: string; meta?: string }[]; cols?: 2 | 3 | 4 }
  | { kind: 'list'; items: string[] }
  | { kind: 'node'; node: ReactNode };

export function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'text':
      return (
        <p className="text-[13px] leading-relaxed text-ink-2 sm:text-sm">
          <Rich text={block.text} />
        </p>
      );
    case 'metrics':
      return (
        <div className={cn('grid grid-cols-2 gap-5', block.items.length >= 4 ? 'lg:grid-cols-4' : 'sm:grid-cols-3')}>
          {block.items.map((m) => (
            <Metric key={m.value + m.label} value={m.value} label={m.label} tone={m.tone} />
          ))}
        </div>
      );
    case 'matrix':
      return (
        <Card variant="raised" className="p-4 sm:p-5">
          <Matrix
            head={block.head}
            rows={block.rows}
            accentRows={block.accentRows}
            numeric={block.numeric}
            minWidth={block.minWidth}
            renderCell={(cell) => <Rich text={cell} />}
          />
        </Card>
      );
    case 'callout':
      return (
        <Callout title={block.title} tone={block.tone}>
          <Rich text={block.text} />
        </Callout>
      );
    case 'cards':
      return (
        <div
          className={cn(
            'grid grid-cols-1 gap-3 sm:gap-4',
            (block.cols ?? 3) === 2 && 'sm:grid-cols-2',
            (block.cols ?? 3) === 3 && 'sm:grid-cols-2 lg:grid-cols-3',
            block.cols === 4 && 'sm:grid-cols-2 lg:grid-cols-4'
          )}
        >
          {block.items.map((item) => (
            <Card key={item.title} variant="raised" className="p-4 sm:p-5">
              <h3 className="mb-1 text-[15px] font-semibold tracking-tight text-ink">{item.title}</h3>
              {item.meta && <p className="mb-2 font-mono text-xs tabular text-accent-ink">{item.meta}</p>}
              <p className="text-[13px] leading-relaxed text-ink-2">
                <Rich text={item.text} />
              </p>
            </Card>
          ))}
        </div>
      );
    case 'list':
      return (
        <ul className="space-y-1.5 pl-4">
          {block.items.map((item) => (
            <li key={item} className="list-disc text-[13px] leading-relaxed text-ink-2 marker:text-accent sm:text-sm">
              <Rich text={item} />
            </li>
          ))}
        </ul>
      );
    case 'node':
      return <>{block.node}</>;
  }
}

export function Blocks({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((block, i) => (
        <BlockView key={i} block={block} />
      ))}
    </>
  );
}
